import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, RefreshCw, MessageSquare } from "lucide-react";
import { LogoMark } from "@/components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUSES = [
  ["pending", "Pending"],
  ["quoted", "Quoted"],
  ["in_print", "In Print"],
  ["delivered", "Delivered"],
];

function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || "").filter(Boolean).join(" ");
  return String(detail);
}

export default function Admin() {
  const [checking, setChecking] = useState(true);
  const [user, setUser] = useState(null);
  const [creds, setCreds] = useState({ email: "", password: "" });
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadEnquiries = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/enquiries`, { withCredentials: true });
      setEnquiries(data);
    } catch (e) {
      if (e.response?.status === 401) setUser(null);
      else toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        await axios.post(`${API}/auth/refresh`, {}, { withCredentials: true });
      } catch {}
      try {
        const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setUser(data);
      } catch {}
      setChecking(false);
    })();
  }, []);

  useEffect(() => {
    if (user) loadEnquiries();
  }, [user]);

  const login = async (e) => {
    e.preventDefault();
    try {
      const { data } = await axios.post(`${API}/auth/login`, creds, { withCredentials: true });
      setUser({ email: data.email, role: "admin" });
      toast.success("Welcome back");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch {}
    setUser(null);
    setEnquiries([]);
  };

  const setStatus = async (id, status) => {
    try {
      const { data } = await axios.patch(`${API}/enquiries/${id}`, { status }, { withCredentials: true });
      setEnquiries((prev) => prev.map((en) => (en.id === id ? { ...en, status: data.status } : en)));
      toast.success("Status updated");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const inputCls =
    "w-full bg-surface border border-edge rounded-sm px-4 py-3.5 text-base text-paper placeholder:text-mist/50 focus:outline-none focus:border-cyanbrand transition-colors";

  if (checking) {
    return (
      <div className="min-h-screen bg-ink grid place-items-center font-body">
        <p className="font-mono text-xs text-mist animate-pulse">CHECKING SESSION…</p>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-ink font-body grid place-items-center px-5">
        <form onSubmit={login} data-testid="admin-login-form" className="w-full max-w-sm border border-edge bg-surface rounded-sm p-8">
          <div className="flex items-center gap-3 mb-8">
            <LogoMark className="w-9 h-9" />
            <div>
              <p className="font-display font-semibold text-paper">Admin Access</p>
              <p className="font-mono text-[10px] tracking-[0.25em] text-mist">PRO PRINT SOLUTION LLP</p>
            </div>
          </div>
          <label htmlFor="admin-email" className="block font-mono text-[11px] uppercase tracking-[0.2em] text-mist mb-2">Email</label>
          <input id="admin-email" required type="email" data-testid="admin-email-input" className={`${inputCls} mb-4`} value={creds.email} onChange={(e) => setCreds({ ...creds, email: e.target.value })} placeholder="admin@proprintsolution.in" />
          <label htmlFor="admin-password" className="block font-mono text-[11px] uppercase tracking-[0.2em] text-mist mb-2">Password</label>
          <input id="admin-password" required type="password" data-testid="admin-password-input" className={`${inputCls} mb-5`} value={creds.password} onChange={(e) => setCreds({ ...creds, password: e.target.value })} placeholder="••••••••" />
          <button
            type="button"
            data-testid="admin-google-button"
            onClick={() => {
              // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
              window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(window.location.origin + "/admin")}`;
            }}
            className="w-full inline-flex items-center justify-center gap-3 bg-white text-ink font-semibold text-sm py-3.5 rounded-sm hover:bg-paper transition-colors mb-5"
          >
            <svg width="17" height="17" viewBox="0 0 24 24" aria-hidden="true">
              <path fill="#4285F4" d="M23.5 12.3c0-.8-.1-1.6-.2-2.3H12v4.5h6.5a5.6 5.6 0 0 1-2.4 3.6v3h3.9c2.3-2.1 3.5-5.2 3.5-8.8z" />
              <path fill="#34A853" d="M12 24c3.2 0 6-1.1 8-2.9l-3.9-3c-1.1.7-2.5 1.2-4.1 1.2-3.1 0-5.8-2.1-6.7-5h-4v3.1C3.3 21.3 7.3 24 12 24z" />
              <path fill="#FBBC05" d="M5.3 14.3a7.2 7.2 0 0 1 0-4.6V6.6h-4a11.9 11.9 0 0 0 0 10.8l4-3.1z" />
              <path fill="#EA4335" d="M12 4.8c1.8 0 3.3.6 4.6 1.8l3.4-3.4C18 1.2 15.2 0 12 0 7.3 0 3.3 2.7 1.3 6.6l4 3.1c.9-2.9 3.6-4.9 6.7-4.9z" />
            </svg>
            Continue with Google
          </button>
          <div className="flex items-center gap-3 mb-4">
            <span className="h-px grow bg-edge" />
            <span className="font-mono text-[10px] tracking-[0.2em] text-mist">OR EMAIL</span>
            <span className="h-px grow bg-edge" />
          </div>
          <button type="submit" data-testid="admin-login-button" className="w-full bg-cyanbrand text-ink font-bold text-sm py-3.5 rounded-sm hover:bg-paper transition-colors">
            Sign In
          </button>
          <a href="/" data-testid="admin-back-link" className="block mt-6 text-center font-mono text-[11px] tracking-[0.2em] text-mist hover:text-cyanbrand transition-colors">
            ← BACK TO SITE
          </a>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ink font-body" data-testid="admin-dashboard">
      <header className="border-b border-edge sticky top-0 bg-ink/90 backdrop-blur-xl z-10">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 h-[72px] flex items-center justify-between">
          <div className="flex items-center gap-3">
            <LogoMark className="w-9 h-9" />
            <div>
              <p className="font-display font-semibold text-paper text-sm">Enquiries</p>
              <p className="font-mono text-[10px] tracking-[0.25em] text-mist">{user.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-3 shrink-0">
            <button onClick={loadEnquiries} data-testid="admin-refresh-button" className="inline-flex items-center gap-2 border border-edge text-mist hover:text-cyanbrand hover:border-cyanbrand font-mono text-[11px] uppercase tracking-[0.2em] px-3 sm:px-4 py-2.5 rounded-sm transition-colors">
              <RefreshCw size={13} className={loading ? "animate-spin" : ""} /> <span className="hidden sm:inline">Refresh</span>
            </button>
            <button onClick={logout} data-testid="admin-logout-button" className="inline-flex items-center gap-2 border border-edge text-mist hover:text-mag hover:border-mag font-mono text-[11px] uppercase tracking-[0.2em] px-3 sm:px-4 py-2.5 rounded-sm transition-colors">
              <LogOut size={13} /> <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 sm:px-8 py-10">
        {enquiries.length === 0 && !loading ? (
          <div className="border border-dashed border-edge rounded-sm py-24 text-center">
            <MessageSquare size={28} className="mx-auto text-mist/40" />
            <p className="mt-4 font-mono text-xs tracking-[0.2em] text-mist">NO ENQUIRIES YET</p>
            <p className="mt-2 text-sm text-mist/60">New enquiries from the website will appear here.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {enquiries.map((en, i) => (
              <article key={en.id} data-testid={`admin-enquiry-row-${i}`} className="border border-edge bg-surface rounded-sm p-5 sm:p-6 grid grid-cols-1 sm:grid-cols-12 gap-4 items-start">
                <div className="sm:col-span-4">
                  <p className="font-display font-medium text-paper text-lg" data-testid={`admin-enquiry-name-${i}`}>{en.name}</p>
                  <p className="font-mono text-[11px] text-mist mt-1">
                    <a href={`tel:${en.phone}`} className="hover:text-cyanbrand" data-testid={`admin-enquiry-phone-${i}`}>{en.phone}</a>
                    {en.email ? <> · {en.email}</> : null}
                  </p>
                  <p className="font-mono text-[10px] text-mist/60 mt-2">
                    {new Date(en.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                  </p>
                </div>
                <div className="sm:col-span-5">
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-cyanbrand">{en.service}</span>
                  <p className="text-sm text-paper/80 mt-2 leading-relaxed">{en.message || "—"}</p>
                </div>
                <div className="sm:col-span-3 sm:justify-self-end">
                  <select
                    value={en.status}
                    data-testid={`admin-enquiry-status-${i}`}
                    onChange={(e) => setStatus(en.id, e.target.value)}
                    className="w-full sm:w-auto bg-ink border border-edge rounded-sm px-3 py-3 sm:py-2.5 text-sm text-paper focus:outline-none focus:border-cyanbrand"
                  >
                    {STATUSES.map(([v, label]) => (
                      <option key={v} value={v}>{label}</option>
                    ))}
                  </select>
                </div>
              </article>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
